/**
 * ComplexityController — the gameplay component that drives FlowBot.
 *
 * Composes four collaborators, each independently testable:
 *   - `complexityModel`   — the pure oracle recurrence (verdicts → band).
 *   - `AssessmentStream`  — the indexed assessment sequence; owns PAD threading.
 *   - `flowRegeneration`  — rest-phrase policy (stuck + accuracy → inject?).
 *   - `complexityMachine` — lifecycle (idle / active / restPending).
 *
 * The controller itself holds almost no state: it wires those four together and
 * exposes a small imperative surface for the scene. Everything numeric lives in
 * the stream or is recomputed from it, so there is no second source of truth to
 * drift.
 *
 * Instantiate behind `FeatureFlag.USE_COMPLEXITY_ADJUSTMENT` — when the flag is
 * off the controller simply is not constructed, and no complexity behaviour can
 * reach the scene.
 *
 * ## On PAD
 *
 * PAD is NOT applied here. The model already encodes it: the band for
 * assessment phrase `p` is driven by `verdict[p - pad]`. The render pipeline
 * supplies exactly that delay for free — `PhraseManager` requests the next
 * phrase at phrase end and consumes it at measure 2 of the following phrase, so
 * a phrase requested at the end of `p` first plays as `p + pad`. Deferring the
 * verdict again inside the controller would double-apply the delay.
 * {@link planNextPhrase} asserts the invariant.
 *
 * That free delay is only correct if the finished phrase is GRADED BEFORE the
 * next request is planned — both happen on the same phrase-end event, and the
 * order between them is the difference between an effective delay of `pad` and
 * one of `pad + 1`. `PhraseManager.createCursor`'s `onFinishPlaying` owns that
 * ordering; the shortfall branch in {@link planNextPhrase} is what catches it
 * if the seam ever moves again.
 */

import { NotifyReactEvents, ReactToPhaserListeners, RemoveListener } from 'Phaser/EventBus2';
import { bandFor, classify, computeTrajectory } from './complexityModel';
import {
  AccuracySource,
  AssessmentBand,
  Band,
  DEFAULT_TIER_CONFIG,
  RATE_RANGE,
  TierConfig,
  Verdict,
  isRateInRange,
} from './complexityModel.types';
import { AssessmentStream, PhraseKind, type StreamEntry } from './AssessmentStream';
import { GradedNote, tallyBreakdown } from './errorTally';
import { restBand, shouldInjectRest } from './flowRegeneration';
import { ComplexityTelemetry, type ServedBand } from './ComplexityTelemetry';
import { NwcsBand, SessionComplexityMap, TierComplexityScore } from './SessionComplexityMap';
import { complexityMachine } from './complexityMachine';
import type { ComplexityPhase } from './complexityMachine.types';
import { createActor, type ActorRefFrom } from 'xstate';

/** What the request path should fetch next. */
export interface PhrasePlan {
  /** Assessment or rest. Rest phrases consume no assessment index. */
  kind: PhraseKind;
  /** Complexity band to request, in normalized [0, 1]. */
  crMin: number;
  crMax: number;
  /** Assessment index this phrase will take, or `undefined` for a rest phrase. */
  idx?: number;
  /**
   * The same band denormalized to raw NWCS for the backend, or `undefined` when
   * the session map is not loaded — in which case the request carries no
   * complexity filter and the backend selects as it did pre-feature.
   */
  requestBand?: NwcsBand;
}

/** Emitted whenever a graded phrase moves (or holds) the band. */
export interface BandChange {
  /** Band now in effect for the next assessment phrase. */
  band: AssessmentBand;
  /** The verdict that caused this evaluation. */
  verdict: Verdict;
  /** Complexity error tally behind the verdict. */
  errorTotal: number;
  /** Assessment index that was just graded. */
  gradedIdx: number;
}

export interface ComplexityControllerConfig {
  /** Per-tier tunables. Defaults to the oracle's active preset. */
  tierConfig?: TierConfig;
  /** Live accuracy provider. See the WMA note on {@link AccuracySource}. */
  accuracySource: AccuracySource;
  /** Fired after each graded phrase, with the band now in effect. */
  onBandChanged?: (change: BandChange) => void;
  /** Fired when a rest phrase is planned, with the band it is drawn from. */
  onRestInjected?: (band: Readonly<Band>) => void;
  /** Diagnostics sink. Defaults to a no-op so tests stay quiet. */
  logger?: (message: string) => void;
}

/**
 * Monotonic tag for controller instances, used only in the log prefix.
 *
 * The scene rebuilds the controller on every `init()` — tier change, but also
 * any scene restart — and a rebuilt controller starts a fresh assessment stream
 * at idx 1. So the same `idx=N` line appearing twice means one of two things,
 * and the tag is what tells them apart in a log capture:
 *
 * - DIFFERENT tags — two sessions, each numbering from 1. Expected after a
 *   remount.
 * - SAME tag — one session planned the same index twice, which only happens
 *   when a request key is reused: {@link AssessmentStream.register} is
 *   idempotent per key, so a repeat keeps the original entry and does NOT
 *   advance `nextIdx`, leaving the next plan to read the same index again.
 *   {@link planNextPhrase} now detects that before doing any work and says so
 *   explicitly. Note that nothing throws on this path — `register` never
 *   throws by design — so an absence of console errors says nothing either way.
 */
let controllerInstanceCount = 0;

export default class ComplexityController {
  private readonly instanceId = ++controllerInstanceCount;
  // Not readonly: the settings panel can change Climb/Drop Rate, Streak Boost
  // and the rest phrase pair mid-session. See `updateConfig`.
  private config: TierConfig;
  private readonly accuracySource: AccuracySource;
  private readonly onBandChanged?: (change: BandChange) => void;
  private readonly onRestInjected?: (band: Readonly<Band>) => void;
  private readonly log: (message: string) => void;
  // Arrow, not `this.log` directly: field initializers run before the
  // constructor body, so `this.log` is not assigned yet at this point.
  private readonly stream = new AssessmentStream((message) =>
    this.log(`AssessmentStream: ${message}`),
  );
  private readonly actor: ActorRefFrom<typeof complexityMachine>;
  private readonly sessionMap = new SessionComplexityMap();
  private readonly telemetry = new ComplexityTelemetry();

  constructor({
    tierConfig = DEFAULT_TIER_CONFIG,
    accuracySource,
    onBandChanged,
    onRestInjected,
    logger,
  }: ComplexityControllerConfig) {
    this.config = tierConfig;
    this.accuracySource = accuracySource;
    this.onBandChanged = onBandChanged;
    this.onRestInjected = onRestInjected;
    const sink = logger ?? (() => undefined);
    this.log = (message) => sink(`[FlowBot#${this.instanceId}] ${message}`);
    this.warnOnOutOfRangeRates();
    this.actor = createActor(complexityMachine);
    this.actor.start();
    ReactToPhaserListeners.UpdateComplexityConfig(this.updateConfig, this);
    // PULL the panel's settings rather than waiting to be pushed them. This
    // controller is rebuilt on every tier change, while the settings are only
    // pushed when they change — so one built after the last change would run on
    // defaults until the user touched a control. Registered above first, so the
    // answer cannot arrive before there is something to receive it.
    NotifyReactEvents.RequestComplexityConfig();
  }

  /**
   * Merge a live config change from the settings panel.
   *
   * Applies from the next planned phrase rather than retroactively: verdicts
   * already recorded keep the rates they were graded under, so changing Climb
   * Rate mid-session moves the trajectory forward from here instead of
   * rewriting where the user has already been. Every call site reads
   * `this.config` afresh, so no recomputation is needed.
   */
  private updateConfig({ config }: { config: Partial<TierConfig> }): void {
    this.config = { ...this.config, ...config };
    this.log(`config updated: ${JSON.stringify(config)}`);
    this.warnOnOutOfRangeRates();
  }

  /**
   * Report rather than reject: a config outside the Climb/Drop Rate bounds still
   * produces a valid trajectory, just one no user could have selected, so the
   * useful outcome is a visible signal rather than a dead session. Both rates
   * are checked; either can be set independently.
   */
  private warnOnOutOfRangeRates(): void {
    (['cai', 'cdi'] as const).forEach((key) => {
      if (!isRateInRange(this.config[key])) {
        this.log(
          `${key}=${this.config[key]} is outside the Climb/Drop Rate range ` +
            `[${RATE_RANGE.min}, ${RATE_RANGE.max}] — running with it anyway`,
        );
      }
    });
  }

  /** Begin a session. Until this is called nothing may be planned or graded. */
  startSession(): void {
    this.actor.send({ type: 'SESSION_STARTED' });
    // "position", not "floor": under centre anchoring complexityStart is the
    // band's midpoint, so 0.20 seeds the band [0.10, 0.30].
    this.log(`session started, seed position=${this.config.complexityStart}`);
  }

  /**
   * Load the tier's raw NWCS scores so planned bands can be denormalized for the
   * backend. Until this is called, plans carry no `requestBand` and phrase
   * selection is unaffected — the controller still tracks the band internally.
   *
   * Safe to call more than once (e.g. a mid-session tier change); the newest
   * scores replace the old.
   */
  loadSessionMap(
    scores: readonly TierComplexityScore[],
    tier?: { levelNumber: number; tierNumber: number },
  ): void {
    this.sessionMap.load(scores);
    const forTier = tier ? ` for L${tier.levelNumber}T${tier.tierNumber}` : '';
    this.log(
      `session map${forTier} ${
        this.sessionMap.isLoaded()
          ? `loaded (${this.sessionMap.scoredPhraseCount()} scored phrases of ${scores.length} received, ` +
            `${this.sessionMap.size()} distinct NWCS values)`
          : 'not usable — complexity filtering disabled'
      }`,
    );
  }

  /** True once a usable session map has been loaded. */
  hasSessionMap(): boolean {
    return this.sessionMap.isLoaded();
  }

  /**
   * Decide what to fetch next, and register it in the assessment stream.
   *
   * Call this once per phrase request, at the point the scene asks for a new
   * phrase. The returned plan's `requestBand` is what the request path should
   * filter on (raw NWCS, or undefined when the session map is not loaded).
   *
   * @param phraseKey  Stable id for this request — the render pipeline's
   *                   `sequenceId`. The same key must be passed to
   *                   {@link ComplexityController.onPhraseEnd} when the phrase
   *                   finishes, so plan and grade line up regardless of any
   *                   phrase-uuid reuse from repeat-avoidance.
   * @throws if the session has not started.
   */
  planNextPhrase(phraseKey: string): PhrasePlan {
    if (this.getPhase() === 'idle') {
      throw new Error('ComplexityController: planNextPhrase before startSession');
    }

    // A request key we have already planned. The render pipeline is supposed to
    // hand out a unique `sequenceId` per request (MUS-406 made the counter
    // module-scoped so a scene remount cannot restart it), so reaching here is a
    // pipeline bug rather than a complexity one.
    //
    // Replay the recorded plan instead of planning afresh. `register` is
    // idempotent per key, so a second plan would keep the original entry and
    // leave `nextIdx` where it was — the request would go out under a band and
    // an index that the stream never agreed to, and telemetry (idempotent on
    // idx) would keep the first row regardless. Replaying keeps the request, the
    // stream and the telemetry telling the same story, and the log line below is
    // what identifies the repeat.
    const planned = this.stream.getEntry(phraseKey);
    if (planned !== undefined) {
      return this.replayPlan(planned);
    }

    const bands = this.getTrajectory();
    const wantsRest =
      this.getPhase() === 'active' &&
      shouldInjectRest(
        {
          bands,
          accuracy: this.accuracySource.currentAccuracy(),
          assessmentsSinceLastRest: this.stream.getAssessmentsSinceLastRest(),
        },
        this.config,
      );

    if (wantsRest) {
      const currentPosition =
        bands.length > 0 ? bands[bands.length - 1].position : this.config.complexityStart;
      const band = restBand(currentPosition, this.config);
      this.stream.register(phraseKey, PhraseKind.Rest, band);
      this.actor.send({ type: 'REST_INJECTED' });
      this.onRestInjected?.(band);
      this.log(`rest phrase injected at ${band.crMin.toFixed(2)}-${band.crMax.toFixed(2)}`);
      return {
        kind: PhraseKind.Rest,
        crMin: band.crMin,
        crMax: band.crMax,
        requestBand: this.sessionMap.denormalizeBand(band.crMin, band.crMax),
      };
    }

    const idx = this.stream.getNextIdx();
    const band = bandFor(idx, this.stream.getVerdicts(), this.config);

    // PAD invariant: the band for phrase `idx` is driven by `verdict[idx - pad]`,
    // so exactly `idx - pad` phrases should be graded when we plan. The two
    // directions are NOT symmetric:
    //
    // - A SHORTFALL is the bug. The driver has not been recorded yet, so
    //   `bandFor` finds no verdict at `idx - pad`, falls through to the hold
    //   branch, and the phrase is fetched from a band that is one verdict stale
    //   — an effective delay of pad + 1 against a model that assumes pad. It
    //   means the request is being planned before the finished phrase is graded;
    //   see the ordering note in this file's header.
    // - A SURPLUS is benign. We are planning later than the bare request/consume
    //   pipeline would, which is exactly what an injected rest phrase does: it
    //   takes a render slot without taking an assessment index. `verdict[idx -
    //   pad]` is still the driver and the band is unchanged; there is simply a
    //   verdict on hand that this row does not use yet.
    //
    // Seeds are exempt by construction: `expected` is 0 for idx <= pad, and
    // nothing is graded before the first phrase ends.
    const graded = this.stream.getGradedCount();
    const expected = Math.max(0, idx - this.config.pad);
    if (graded < expected) {
      this.log(
        `PAD invariant VIOLATED: planning idx=${idx} with ${graded} graded, expected ${expected} — ` +
          `driver verdict[${idx - this.config.pad}] is not recorded yet, so this phrase is drawn ` +
          `one verdict stale (effective delay ${this.config.pad + (expected - graded)})`,
      );
    } else if (graded > expected) {
      this.log(
        `planning idx=${idx} with ${graded} graded, expected ${expected} — behind the pipeline ` +
          `(rest-phrase interleave); band unaffected`,
      );
    }

    // Narrowed to the band alone: `bandFor` also returns idx/driver/cooldown,
    // which are trajectory bookkeeping and have no meaning once replayed back
    // per phrase.
    this.stream.register(phraseKey, PhraseKind.Assessment, {
      position: band.position,
      crMin: band.crMin,
      crMax: band.crMax,
    });
    // Denormalize before recording: telemetry needs to know whether the request
    // actually carried a filter, not just what was planned. The two diverge
    // whenever the session map is not loaded yet.
    const requestBand = this.sessionMap.denormalizeBand(band.crMin, band.crMax);
    this.telemetry.recordPlan({
      idx,
      position: band.position,
      crMin: band.crMin,
      crMax: band.crMax,
      driver: band.driver,
      cooldown: band.cooldown,
      restsBefore: this.getRestCount(),
      requestBand,
    });
    if (requestBand === undefined) {
      this.log(`idx=${idx} requested UNBANDED — no session map at plan time`);
    }
    return {
      kind: PhraseKind.Assessment,
      crMin: band.crMin,
      crMax: band.crMax,
      idx,
      requestBand,
    };
  }

  /**
   * Re-answer a plan for a request key that was already planned, from what the
   * stream recorded the first time. See the duplicate-key branch in
   * {@link planNextPhrase}.
   *
   * The recorded band is authoritative: it is the band the phrase was actually
   * drawn from, and what {@link getPlannedBand} will keep reporting for it. A
   * band is written at every `register` call site, so the recompute below is
   * only a floor under a shape that should not occur.
   */
  private replayPlan(entry: Readonly<StreamEntry>): PhrasePlan {
    const isRest = entry.kind === PhraseKind.Rest;
    const band =
      entry.band ??
      bandFor(entry.idx ?? this.stream.getNextIdx(), this.stream.getVerdicts(), this.config);
    this.log(
      `request key ${entry.uuid} was already planned as ` +
        `${isRest ? 'a rest phrase' : `idx=${String(entry.idx)}`} — replaying that plan. ` +
        `The pipeline reused a request key; the assessment index does not advance for it, ` +
        `so the next plan will read the same index again`,
    );
    return {
      kind: entry.kind,
      crMin: band.crMin,
      crMax: band.crMax,
      idx: entry.idx,
      requestBand: this.sessionMap.denormalizeBand(band.crMin, band.crMax),
    };
  }

  /**
   * Grade a phrase that has finished playing.
   *
   * Rest phrases are recognised and deliberately skipped — they receive no
   * verdict, do not move the band, and do not consume a PAD slot.
   *
   * @param phraseKey the same key passed to {@link planNextPhrase} for this
   *              phrase (the render pipeline's `sequenceId`).
   * @param notes the phrase's graded notes, read from `EventStream`. The tally
   *              uses complexity weights (red 1.0 / yellow 0.5), not
   *              `EventStream`'s accuracy scale.
   */
  onPhraseEnd(phraseKey: string, notes: readonly GradedNote[]): void {
    const entry = this.stream.getEntry(phraseKey);
    if (entry === undefined) {
      this.log(`ignoring unknown phrase ${phraseKey}`);
      return;
    }

    if (entry.kind === PhraseKind.Rest) {
      this.actor.send({ type: 'REST_COMPLETED' });
      this.log(`rest phrase complete — band untouched`);
      return;
    }

    // A phrase can finish playing more than once — the cursor re-enters the same
    // phrase on rollback / resume, and each re-entry fires onFinishPlayingPhrase
    // again (the phrase-request path dedupes by index and ATA by uuid for the
    // same reason). One assessment phrase is one verdict, graded on first
    // completion, so ignore any later finish rather than double-grading.
    if (entry.verdict !== undefined) {
      this.log(`phrase ${phraseKey} already graded — ignoring duplicate finish`);
      return;
    }

    const { reds, yellows, errorTotal } = tallyBreakdown(notes);
    const verdict = classify(errorTotal, this.config);
    this.stream.grade(phraseKey, verdict, errorTotal);

    const gradedIdx = entry.idx as number;
    this.telemetry.recordGrade(gradedIdx, {
      verdict,
      errorTotal,
      redNotes: reds,
      yellowNotes: yellows,
      accuracy: this.accuracySource.currentAccuracy(),
    });
    const band = bandFor(gradedIdx + this.config.pad, this.stream.getVerdicts(), this.config);
    this.log(
      `idx=${gradedIdx} errors=${errorTotal} (${reds}R ${yellows}Y) verdict=${verdict} ` +
        `-> band ${band.crMin.toFixed(2)}-${band.crMax.toFixed(2)} (applies at idx=${band.idx})`,
    );
    this.onBandChanged?.({ band, verdict, errorTotal, gradedIdx });
  }

  /** The full band trajectory so far — telemetry, tests, and the stuck check. */
  getTrajectory(): AssessmentBand[] {
    const graded = this.stream.getGradedCount();
    if (graded === 0) return [];
    return computeTrajectory(this.stream.getVerdicts(), this.config, graded);
  }

  /** Band that will apply to a given assessment index. */
  getBand(idx: number): AssessmentBand {
    return bandFor(idx, this.stream.getVerdicts(), this.config);
  }

  /**
   * The band a specific planned phrase was drawn from, keyed by the render
   * pipeline's `sequenceId`.
   *
   * This is the band as it was at plan time — deliberately not a recomputation,
   * because the trajectory moves on as later phrases are graded and the caller
   * wants what the phrase *now playing* was actually drawn from. Rest phrases
   * return their (lowered) rest band; unknown keys return `undefined`.
   */
  getPlannedBand(phraseKey: string): Readonly<Band> | undefined {
    return this.stream.getEntry(phraseKey)?.band;
  }

  /**
   * The full served-band record for a planned phrase, flattened to the shape
   * the analytics event carries. Keyed by the render pipeline's `sequenceId`,
   * like {@link getPlannedBand}.
   *
   * Everything here is fixed at plan time, so this answers as soon as the
   * phrase has been requested — well before it is played, let alone graded.
   * That matters: the caller stamps it onto the phrase when the phrase data is
   * built, so the value is already aboard by the time the completed event is
   * dispatched, rather than racing the phrase-end handler for it.
   *
   * `undefined` for rest phrases and unknown keys. Rest phrases are drawn below
   * the band without moving it and take no assessment index, so they have no
   * served band in the sense the event stream means — see {@link PhaseRecord}.
   */
  getServedBand(phraseKey: string): ServedBand | undefined {
    const idx = this.stream.getEntry(phraseKey)?.idx;
    if (idx === undefined) return undefined;
    const row = this.telemetry.row(idx);
    if (row === undefined) return undefined;
    return {
      assessment_idx: row.idx,
      served_position: row.position,
      served_band_min: row.crMin,
      served_band_max: row.crMax,
      served_band_min_nwcs: row.requestBand?.min,
      served_band_max_nwcs: row.requestBand?.max,
      // Seed rows have no verdict at `idx - pad` to point at. Null rather than
      // undefined so the field is present and explicitly empty on the wire.
      served_driver_verdict: row.driver ?? null,
      served_cooldown: row.cooldown,
      served_rests_before: row.restsBefore,
      banded: row.banded,
    };
  }

  /**
   * The band the session opens on, before any phrase has been planned. Used to
   * seed the header progress bar so it does not start empty.
   */
  getSeedBand(): AssessmentBand {
    return bandFor(1, [], this.config);
  }

  getPhase(): ComplexityPhase {
    return this.actor.getSnapshot().value as ComplexityPhase;
  }

  getRestCount(): number {
    return this.actor.getSnapshot().context.restsInjected;
  }

  /**
   * Whether the phrase registered under `phraseKey` is a rest phrase. Used by
   * the scene at play time (keyed by the render pipeline's sequenceId) to drive
   * the rest-phrase UI. Unknown keys are not rest phrases.
   */
  isRest(phraseKey: string): boolean {
    return this.stream.isRest(phraseKey);
  }

  /** Read-only view of the assessment stream, for telemetry and tests. */
  getStream(): AssessmentStream {
    return this.stream;
  }

  getTierConfig(): TierConfig {
    return this.config;
  }

  /**
   * Clear all state. The scene instance is reused across tier changes and class
   * field initialisers do not re-run, so this must be called on tier change.
   */
  /** Per-phrase telemetry recorder — for oracle comparison. See
   *  {@link ComplexityTelemetry} and `installFlowbotConsole`. */
  getTelemetry(): ComplexityTelemetry {
    return this.telemetry;
  }

  reset(): void {
    this.stream.reset();
    this.sessionMap.load([]);
    this.telemetry.reset();
    this.actor.send({ type: 'RESET' });
    this.log('reset');
  }

  destroy(): void {
    RemoveListener('UpdateComplexityConfig');
    this.actor.stop();
  }
}
